/**
 * Version history for one file.
 *
 * Lives inside the viewer rather than on its own screen: the question "what did
 * this look like before?" is asked while looking at the file, not while
 * browsing a folder.
 *
 * Nothing here is a cryptographic special case. Every version of a file shares
 * one content key — `content_key_wrapped` is on the node, not the revision —
 * so restoring is a pointer move and downloading an old version is the ordinary
 * download against a different R2 prefix. The one thing that must not be got
 * wrong is pairing a manifest with its own chunks: the nonce base is per
 * revision, and mixing two would fail authentication rather than quietly
 * producing garbage, which is the failure mode you want.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, errorText } from '../api';
import { ds } from '../ds';
import { downloadRevision, type DecryptedNode } from '../nodes';
import { formatBytes, formatWhen, monoPlain, tableHead } from '../theme';
import { SkeletonLines } from './Skeleton';
import { useT } from '../i18n';

type Revisions = Awaited<ReturnType<typeof api.nodes.revisions>>;

export function Versions({
  node,
  nodeKey,
  onRestored
}: {
  node: DecryptedNode;
  nodeKey: CryptoKey | null;
  onRestored: () => void;
}) {
  const t = useT();
  const { Button, Icon } = ds();
  const [state, setState] = useState<Revisions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await api.nodes.revisions(node.id));
    } catch (err) {
      setError(errorText(err, 'Could not load previous versions.'));
    }
  }, [node.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return <div style={{ ...monoPlain, color: 'var(--danger-600)', padding: '14px 22px' }}>{error}</div>;
  }
  if (!state) {
    return (
      <div style={{ padding: '14px 22px' }}>
        <SkeletonLines lines={3} label={t('versions.loading-versions')} />
      </div>
    );
  }

  const older = state.revisions.filter((r) => !r.current);

  return (
    <div style={{ padding: '14px 22px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <span style={tableHead}>{t('versions.versions')}</span>
        <span style={monoPlain}>
          {state.kept === 1
            ? 'your plan keeps the current version only'
            : `keeping the last ${state.kept}`}
        </span>
      </div>

      {state.kept === 1 && (
        // Said plainly rather than hidden behind a lock icon: the reason there
        // is no history is the plan, and that is worth knowing before you
        // overwrite something.
        <div style={{ ...monoPlain, lineHeight: 1.6 }}>
          {t('versions.replacing-this-file-will-overwrite')}
        </div>
      )}

      {state.kept > 1 && older.length === 0 && (
        <div style={monoPlain}>{t('versions.no-previous-versions-yet')}</div>
      )}

      {older.map((rev) => (
        <div
          key={rev.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '9px 0',
            borderTop: '1px solid var(--border-subtle)',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontSize: 13.5, color: 'var(--text-body)' }}>
              {rev.label || formatWhen(rev.createdAt)}
            </div>
            <div style={monoPlain}>
              {formatBytes(rev.plainSizeBytes)}
              {rev.label ? ` · ${formatWhen(rev.createdAt)}` : ''}
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            disabled={!nodeKey || busy !== null}
            onClick={async () => {
              if (!nodeKey) return;
              setBusy(rev.id);
              try {
                await downloadRevision(node, nodeKey, rev.id, rev.createdAt);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Could not decrypt that version.');
              } finally {
                setBusy(null);
              }
            }}
          >
            {busy === rev.id ? 'Working…' : 'Download'}
          </Button>

          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            onClick={async () => {
              setBusy(rev.id);
              try {
                await api.nodes.restoreRevision(node.id, rev.id);
                await load();
                onRestored();
              } catch (err) {
                setError(errorText(err, 'Could not restore that version.'));
              } finally {
                setBusy(null);
              }
            }}
          >
            Restore
          </Button>

          <button
            aria-label={t('versions.delete-this-version')}
            title={t('versions.delete-this-version')}
            disabled={busy !== null}
            onClick={async () => {
              if (!window.confirm('Delete this version for good?'))
                return;
              setBusy(rev.id);
              try {
                await api.nodes.deleteRevision(node.id, rev.id);
                await load();
              } catch (err) {
                setError(errorText(err, 'Could not delete that version.'));
              } finally {
                setBusy(null);
              }
            }}
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              border: 'none',
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'pointer'
            }}
          >
            <Icon name="trash-2" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
